import {
  addItemToCollection,
  updateItemInCollection,
} from "directus/src/operations";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contractSchema } from "./schema";
import { fetchContractData } from "./read";
import { useMemo } from "react";
import { getRequiredFields } from "components/form/utils";
import { toast } from "sonner";
import { invalidateAllQueries } from "utils/utils";

function buildPayload(values) {
  if (!values || typeof values !== "object") return {};

  return {
    ...Object.fromEntries(
      Object.entries(values).filter(
        ([_, v]) => v !== "" && v !== undefined && v !== null
      )
    ),
  };
}

export const useContractForm = ({ contractId, defaultProjectId }) => {
  const form = useForm({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      name: "",
      status: "draft",
      type: "",
      project: defaultProjectId,
    },
  });

  const requiredFields = useMemo(
    () => getRequiredFields(contractSchema),
    [contractSchema]
  );

  const createMutation = useMutation({
    mutationFn: async (item) => {
      const payload = buildPayload(item);
      return await addItemToCollection("contracts", payload);
    },
    onSuccess: () => {
      toast.success("Contract Created Successfully");
      invalidateAllQueries();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ contractId, values }) => {
      const payload = buildPayload(values);
      return await updateItemInCollection("contracts", contractId, payload);
    },
    onSuccess: () => {
      toast.success("Contract Updated Successfully");
      invalidateAllQueries();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = async (context) => {
    const item = { ...context.values };
    return await createMutation.mutateAsync(item);
  };

  const handleUpdate = async (id, context) => {
    const values = { ...context.dirtyValues };
    return await updateMutation.mutateAsync({ contractId: id, values });
  };

  const handleRead = async (id) => {
    const response = await fetchContractData({ contractId: id });
    return { ...response };
  };

  return {
    form,
    requiredFields,
    handleCreate,
    handleUpdate,
    handleRead,
  };
};
